import { useMemo, useRef, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from '@/components/ui/spinner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { Sparkles, ChevronsUpDown, Check, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import {
    useGetDocumentableModelsQuery,
    useGenerateDocumentationWithAiMutation,
    useLazyGetDocumentationAiTaskQuery,
} from '@/store/documentationApi';
import type { AiGenerateResult } from '@/types/Documentation';

const AI_TASK_POLL_INTERVAL_MS = 10_000;

interface AiGenerateDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** נקרא כשהמשתמש מאשר להחיל את התוצאה על העורך (ללא שמירה אוטומטית) */
    onApply: (title: string, content: string) => void;
    /** כותרת המסמך הקיים בעורך (אם עורכים מסמך קיים ולא יוצרים חדש) — נשלחת כהקשר ל-AI */
    existingTitle?: string;
    /** תוכן המסמך הקיים בעורך (כולל שינויים שטרם נשמרו) — נשלח כהקשר ל-AI כדי לשמר מבנה/תוכן קיים */
    existingContent?: string;
}

/**
 * דיאלוג "צור/ערוך עם AI" — בחירת מודל Django + פרומפט חופשי, הפעלת Documentation Agent
 * מול Gemini, והצגת תוצאה (כותרת+תוכן) לאישור ידני של המשתמש לפני מילוי בעורך.
 */
export function AiGenerateDialog({ open, onOpenChange, onApply, existingTitle, existingContent }: AiGenerateDialogProps) {
    const { data: models, isLoading: isLoadingModels } = useGetDocumentableModelsQuery(undefined, { skip: !open });
    const [generate, { isLoading: isGenerating }] = useGenerateDocumentationWithAiMutation();
    const [getTaskStatus] = useLazyGetDocumentationAiTaskQuery();

    const hasExistingDocument = Boolean(existingContent && existingContent.trim().length > 0);

    const [modelOpen, setModelOpen] = useState(false);
    const [modelLabel, setModelLabel] = useState('');
    const [prompt, setPrompt] = useState('');
    const [result, setResult] = useState<{ title: string; content: string; rounds_used: number; traceCount: number } | null>(null);
    const [isWaitingForResult, setIsWaitingForResult] = useState(false);
    const [showTrace, setShowTrace] = useState(false);
    const [trace, setTrace] = useState<Array<{ tool: string; args: Record<string, unknown>; result_chars: number; elapsed_sec: number }>>([]);
    const pollingRunRef = useRef(0);

    const selectedModel = useMemo(() => models?.find((m) => m.label === modelLabel), [models, modelLabel]);

    function resetAndClose() {
        pollingRunRef.current += 1;
        setModelLabel('');
        setPrompt('');
        setResult(null);
        setIsWaitingForResult(false);
        setTrace([]);
        setShowTrace(false);
        onOpenChange(false);
    }

    function showResult(generatedResult: AiGenerateResult) {
        setResult({
            title: generatedResult.title,
            content: generatedResult.content,
            rounds_used: generatedResult.rounds_used,
            traceCount: generatedResult.trace?.length ?? 0,
        });
        setTrace(generatedResult.trace ?? []);
    }

    async function waitForTask(taskId: string, pollingRun: number) {
        while (pollingRunRef.current === pollingRun) {
            await new Promise((resolve) => window.setTimeout(resolve, AI_TASK_POLL_INTERVAL_MS));
            if (pollingRunRef.current !== pollingRun) return;

            const task = await getTaskStatus(taskId, false).unwrap();
            if (task.state === 'SUCCESS' && task.result) {
                showResult(task.result);
                return;
            }
            if (task.state === 'FAILURE') {
                throw new Error(task.detail || 'שגיאה ביצירת תיעוד עם AI');
            }
        }
    }

    async function handleGenerate() {
        if (!modelLabel) {
            toast.error('יש לבחור מודל');
            return;
        }
        if (!prompt.trim()) {
            toast.error('יש להזין פרומפט');
            return;
        }
        const pollingRun = pollingRunRef.current + 1;
        pollingRunRef.current = pollingRun;
        setIsWaitingForResult(true);
        try {
            const task = await generate({
                model_label: modelLabel,
                prompt: prompt.trim(),
                ...(hasExistingDocument ? { existing_title: existingTitle, existing_content: existingContent } : {}),
            }).unwrap();
            if (task.result) {
                showResult(task.result);
            } else if (task.task_id) {
                await waitForTask(task.task_id, pollingRun);
            } else {
                throw new Error('השרת לא החזיר מזהה פעולה');
            }
        } catch (err) {
            if (pollingRunRef.current === pollingRun) {
                const message = (err as { data?: { detail?: string }; message?: string })?.data?.detail
                    || (err as Error)?.message
                    || 'שגיאה ביצירת תיעוד עם AI';
                toast.error(message);
            }
        } finally {
            if (pollingRunRef.current === pollingRun) {
                setIsWaitingForResult(false);
            }
        }
    }

    function handleApply() {
        if (!result) return;
        onApply(result.title, result.content);
        toast.success('התוכן הוחל בעורך — ניתן לערוך ולשמור');
        resetAndClose();
    }

    return (
        <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(v) : resetAndClose())}>
            <DialogContent className="max-w-2xl w-full max-h-[90vh] flex flex-col gap-0 p-0" dir="rtl">
                <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
                    <DialogTitle className="text-base font-semibold flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-primary" />
                        צור/ערוך עם AI
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground">
                        בחר מודל Django ותאר מה לתעד. ה-AI יחקור את קוד הפרויקט ויציע טיוטת תיעוד — התוצאה תוצג לפני החלה בעורך.
                    </p>
                    {hasExistingDocument && (
                        <Badge variant="outline" className="w-fit gap-1.5 font-normal text-xs">
                            <Sparkles className="h-3 w-3" />
                            עריכת מסמך קיים ({(existingContent ?? '').length.toLocaleString()} תווים) תישלח כהקשר ל-AI — המבנה הקיים יישמר, רק הסעיף הרלוונטי לפרומפט ישתנה
                        </Badge>
                    )}
                </DialogHeader>

                <div className="flex flex-col gap-4 px-6 py-4 overflow-y-auto flex-1">
                    {/* model select */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium">מודל Django</label>
                        <Popover open={modelOpen} onOpenChange={setModelOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    type="button"
                                    role="combobox"
                                    aria-expanded={modelOpen}
                                    disabled={isLoadingModels}
                                    className="w-full justify-between text-sm font-normal h-10"
                                >
                                    <span className="truncate">
                                        {isLoadingModels
                                            ? 'טוען מודלים…'
                                            : selectedModel
                                                ? `${selectedModel.verbose_name} (${selectedModel.label})`
                                                : 'בחר מודל…'}
                                    </span>
                                    <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                                <Command dir="rtl">
                                    <CommandInput placeholder="חיפוש מודל לפי שם…" className="text-sm" dir="rtl" />
                                    <CommandList>
                                        <CommandEmpty>לא נמצאו מודלים</CommandEmpty>
                                        <CommandGroup>
                                            {(models ?? []).map((m) => (
                                                <CommandItem
                                                    key={m.label}
                                                    value={`${m.label} ${m.verbose_name}`}
                                                    onSelect={() => {
                                                        setModelLabel(m.label);
                                                        setModelOpen(false);
                                                    }}
                                                    className="text-sm flex items-center gap-2"
                                                >
                                                    <Check className={`h-4 w-4 shrink-0 ${modelLabel === m.label ? 'opacity-100' : 'opacity-0'}`} />
                                                    <span className="flex flex-col items-start gap-0.5 min-w-0">
                                                        <span className="font-medium truncate w-full text-right">{m.verbose_name}</span>
                                                        <span className="text-xs text-muted-foreground truncate w-full text-right">{m.label}</span>
                                                    </span>
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </div>

                    {/* prompt */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium">פרומפט</label>
                        <Textarea
                            dir="rtl"
                            placeholder="לדוגמה: תעד את ה-API של המודל הזה, כולל endpoints, שדות, הרשאות ודוגמת שימוש"
                            rows={4}
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                        />
                    </div>

                    <Button onClick={handleGenerate} disabled={isGenerating || isWaitingForResult} className="gap-1.5 w-fit self-start">
                        {isGenerating || isWaitingForResult ? <Spinner className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
                        {isGenerating || isWaitingForResult ? 'מפיק תיעוד…' : 'צור תיעוד'}
                    </Button>

                    {/* result preview */}
                    {result && (
                        <div className="flex flex-col gap-2 rounded-lg border bg-muted/20 p-4">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <p className="text-sm font-semibold">{result.title || '(ללא כותרת)'}</p>
                                <div className="flex items-center gap-2">
                                    <Badge variant="secondary" className="font-normal">{result.rounds_used} סבבי חקירה</Badge>
                                    {result.traceCount > 0 && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 gap-1 text-xs text-muted-foreground"
                                            onClick={() => setShowTrace((v) => !v)}
                                        >
                                            <ListChecks className="h-3.5 w-3.5" />
                                            {showTrace ? 'הסתר' : 'הצג'} מעקב ({result.traceCount})
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <div dir="rtl" className="text-sm text-muted-foreground max-h-48 overflow-y-auto whitespace-pre-wrap">
                                {result.content}
                            </div>
                            {showTrace && (
                                <div className="flex flex-col gap-1 border-t pt-2 mt-1">
                                    {trace.map((t, i) => (
                                        <div key={i} className="text-xs text-muted-foreground flex items-center gap-2">
                                            <span className="font-mono">{t.tool}</span>
                                            <span className="truncate">{JSON.stringify(t.args)}</span>
                                            <span className="shrink-0">{t.result_chars} תווים</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter className="px-6 py-4 border-t shrink-0 flex gap-2 justify-start flex-row-reverse">
                    <Button onClick={handleApply} disabled={!result} className="gap-1">
                        החל בעורך
                    </Button>
                    <Button variant="ghost" onClick={resetAndClose}>
                        ביטול
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
